/* eslint-disable react/jsx-props-no-spreading */
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { camelCase, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

// import * as AppPropTypes from '../../lib/PropTypes';
import { useContest } from '../../contexts/ContestContext';
import { useLiveDisplayContext } from '../../contexts/LiveDisplayContext';
import { useThemeClassName, useTheme } from '../../contexts/ThemeContext';
import Logo from '../icons/Logo';
import Slider from '../partials/Slider';
import Banner from './banners/Banner';
import Slide from './slides/Slide';

import styles from '../../../styles/displays/sport.module.scss';

const propTypes = {
    // page: AppPropTypes.page.isRequired,
    vertical: PropTypes.bool.isRequired,
};

function SportDisplay({ vertical }) {
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const { logo = null } = useTheme();
    const themeClassName = useThemeClassName(styles);

    const { component: logoComponent } = logo || {};
    const { url = null } = useContest();

    const { display = null, games = [] } = useLiveDisplayContext();

    const {
        // venue: initialVenue = null,
        banners = null,
        slides = null,
        game = null,
        participants = 0,
        slug = null,
    } = display || {};

    useEffect(() => {
        if (game === null) {
            const [firstGame = null] = games || [];
            if (firstGame !== null) {
                navigate(route('display.game', { game: firstGame.id, display: slug }));
            }
        }
    }, [game, games]);

    const onClickGame = useCallback(
        (newGame) => {
            navigate(route('display.game', { game: newGame.id, display: slug }));
        },
        [slug],
    );

    const currentSlides = useMemo(() => sortBy(slides, (slide) => slide.period), []);

    const finalBanners = useMemo(() => {
        const size = 12;
        const currentGame =
            (games || []).find((it) => game !== null && it.id === game.id) || game || null;
        const otherGames = (games || []).filter((it) => game === null || it.id !== game.id);
        const items = Array(Math.ceil(otherGames.length / size))
            .fill()
            .map((_, index) => index * size)
            .map((start) => otherGames.slice(start, start + size))
            .map((its) => (currentGame !== null ? [currentGame].concat(its) : its))
            .filter((its) => its !== null && its.length > 0)
            .map((its) => ({ games: its, type: 'games' }));
        if (items.length === 0 && currentGame !== null) {
            items.push({ games: [currentGame], type: 'games' });
        }
        return items.concat(banners);
    }, [game, games, banners]);

    const siteUrl = useMemo(
        () =>
            url !== null
                ? url.replace(/https:\/\/|dev\.|\.test:8080/g, '').replace('/\\', '')
                : null,
        [url],
    );

    // TO DEBUG SPECIFIC SLIDES
    // const finalSlides = currentSlides.filter((s) => s.type === 'video');
    const finalSlides = currentSlides;
    // console.log('finalSlides', finalSlides);

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles.vertical]: vertical,
                },
            ])}
        >
            <div className={styles.top}>
                <aside className={styles.sidebar}>
                    <Logo
                        component={logoComponent}
                        className={classNames([
                            styles.logo,
                            {
                                [styles[camelCase(logoComponent)]]: logoComponent !== null,
                            },
                        ])}
                    />
                    <div className={styles.content}>
                        {participants !== null ? (
                            <div className={styles.participants}>
                                <p className={styles.number}>{participants || 0}</p>
                                <span className={styles.label}>
                                    <FormattedMessage
                                        defaultMessage="Active players"
                                        description="Display label"
                                    />
                                </span>
                            </div>
                        ) : null}
                        {siteUrl !== null ? (
                            <div
                                className={classNames([
                                    styles.url,
                                    {
                                        [styles.long]: siteUrl.length > 15,
                                    },
                                ])}
                            >
                                {siteUrl}
                            </div>
                        ) : null}
                    </div>
                </aside>
                <main className={styles.slide}>
                    {game !== null ? (
                        <Slider
                            items={finalSlides}
                            component={Slide}
                            itemProps={{ game, vertical }}
                            duration={8}
                        />
                    ) : null}
                </main>
            </div>
            <footer className={styles.footer}>
                <Slider
                    items={finalBanners}
                    component={Banner}
                    direction="bottom"
                    itemProps={{ game, siteUrl, logoComponent, onClickGame, vertical }}
                    duration={16}
                />
            </footer>
        </div>
    );
}

SportDisplay.propTypes = propTypes;

export default SportDisplay;
